export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-3xl md:text-4xl font-bold">Terms of Service</h1>
      <p className="text-sm text-slate-500 mt-2">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

      <p className="mt-6 text-slate-300">
        These terms apply when you use DraftLion. By purchasing or using the service, you agree to them.
        We've kept them short and in plain English.
      </p>

      <Section title="The service">
        <p>
          DraftLion provides AI-assisted fantasy football draft recommendations by connecting
          Anthropic's Claude to Sleeper's public fantasy football API. We're an independent
          service — not affiliated with, endorsed by, or sponsored by Sleeper, Anthropic, or the NFL.
        </p>
      </Section>

      <Section title="Pricing and renewal">
        <p>
          DraftLion is offered on two plans: <strong>$3.99 USD per month</strong> or{' '}
          <strong>$22.99 USD per year</strong>. Both plans include a <strong>3-day free
          trial</strong> for new customers. We collect a payment method when you start the
          trial but do not charge it until the trial ends.
        </p>
        <p className="mt-3">
          <strong>Free trials are limited to one per customer.</strong> If you have
          subscribed to DraftLion before — including a trial you cancelled — your new
          subscription begins immediately and your payment method is charged at signup,
          with no trial period.
        </p>
        <p className="mt-3">
          Your subscription auto-renews at the end of each billing period (monthly or
          yearly) unless you cancel. We email you before the first charge at the end of
          your trial, and again before each renewal, so you're never surprised by a charge.
        </p>
        <p className="mt-3">
          You can cancel anytime through the{' '}
          <a className="text-amber-400 hover:text-amber-300" href="/manage">manage subscription page</a>{' '}
          If you cancel <strong>during the free trial</strong>, you will
          not be charged and your access ends at the time you cancel — the trial does not
          continue after cancellation. If you cancel <strong>after a paid period has
          begun</strong>, your access continues until the end of the period you've already
          paid for.
        </p>
        <p className="mt-3">
          You can switch between the monthly and yearly plans at any time from the manage
          subscription page. Stripe handles proration automatically.
        </p>
      </Section>

      <Section title="Refunds">
        <p>
          We do not offer refunds at this time.
        </p>
      </Section>

      <Section title="Acceptable use">
        <p>You agree not to:</p>
        <ul className="mt-2 list-disc list-inside text-slate-300 space-y-1">
          <li>Share your access with others — your subscription is for one user</li>
          <li>Resell access to DraftLion as part of another product</li>
          <li>Attempt to circumvent the rate limits</li>
          <li>Use the service to abuse, overload, or attack Sleeper's API</li>
        </ul>
        <p className="mt-3">
          We reserve the right to suspend or revoke any access that we believe, in our sole discrection,
          has violated or may have violated these terms, without prior notice. 
        </p>
      </Section>

      <Section title="Service availability">
        <p>
          We aim for high uptime but make no guarantee. DraftLion runs on AWS and depends on
          third-party services that are outside our control. An outage, rate limit, or change at any of
          those will affect the service.
        </p>
        <p className="mt-3">
          We're not liable for missed picks, lost league matchups, or other consequences of
          service unavailability — including unavailability caused by your Claude plan
          hitting its usage cap during your draft.
        </p>
        <p className="mt-3">
          Translation: don't bet your fantasy season on us alone — we're here to help, not to
          guarantee a championship.
        </p>
      </Section>

      <Section title="Disclaimer">
        <p>
          DraftLion's recommendations are AI-generated based on statistical models and live data.
          They are <strong>not professional advice</strong>. Fantasy football outcomes depend on
          many factors beyond any tool's control. Use the recommendations as input to your own
          decisions.
        </p>
      </Section>

      <Section title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, DraftLion's total liability to you is limited
          to the amount you paid us in the previous 12 months.
        </p>
      </Section>

      <Section title="Changes to these terms">
        <p>
          If we change these terms in a way that affects existing customers, we'll email you
          before the changes take effect.
        </p>
      </Section>

      <Section title="Governing law">
        <p>
          These terms are governed by the laws of the State of Illinois, USA.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions? <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">support@thedraftlion.com</a>.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold text-white">{title}</h2>
      <div className="mt-3 text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}
